import React, { useState, useEffect, useRef } from 'react';
import { CustomLayoutProps } from '../../../../types/summarizer';
import { useSummarizer } from '../../../../core/store/useSummarizer';
import { useMediaQuery } from 'usehooks-ts';
import { cn } from '@components/lib/utils';

const TextSummarizerLayout: React.FC<CustomLayoutProps> = ({
  children,
  className = '',
  config,
}) => {
  const { currentSummary } = useSummarizer();
  const outputPanelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('text-input-left-panel-width');
    return saved ? parseInt(saved) : 600;
  });

  const panelConfig = {
    min: 400,
    max: 800,
    value: leftPanelWidth,
  };

  // Auto-scroll to output panel when summary is generated
  useEffect(() => {
    if (currentSummary && outputPanelRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        outputPanelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [currentSummary]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const startX = e.clientX;
    const startWidth = leftPanelWidth;

    const mouseMoveHandler = (e: MouseEvent) => {
      const dx = e.clientX - startX;
      const newWidth = startWidth + dx;
      if (newWidth >= panelConfig.min && newWidth <= panelConfig.max) {
        localStorage.setItem('text-input-left-panel-width', newWidth.toString());
        setLeftPanelWidth(newWidth);
      }
    };

    const mouseUpHandler = () => {
      document.removeEventListener('mouseup', mouseUpHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  const childrenArray = React.Children.toArray(children);
  const inputComponent = childrenArray[0];
  const outputComponent = childrenArray[1];

  // Clone output component and pass config prop if it exists
  const outputComponentWithConfig =
    outputComponent && React.isValidElement(outputComponent)
      ? React.cloneElement(outputComponent, { config } as any)
      : outputComponent;

  return (
    <div className={`text-input-section ${className}`}>
      {/* <div className="text-center mb-8">
        <p className="text-3xl font-bold text-gray-900 mb-2">{config.title}</p>
        <p className="text-lg text-gray-600">{config.description}</p>
      </div> */}

      <div
        ref={containerRef}
        className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col md:!flex-row md:h-[600px] overflow-hidden border-b"
      >
        <div
          className="border-r p-4 flex flex-col overflow-hidden"
          style={{ width: !isMobile ? `${leftPanelWidth}px` : '100%' }}
        >
          {inputComponent}
        </div>

        {/* Resize Handle */}
        {!isMobile && (
          <div className="relative flex w-[1px] bg-transparent">
            <div className="h-full w-[1px] bg-gray-300"></div>
            <div
              className="absolute -left-1 top-1/2 -translate-y-1/2 cursor-col-resize"
              onMouseDown={handleMouseDown}
            >
              <div className="h-8 w-2 rounded-[30px] border border-gray-300 bg-gray-300 hover:bg-gray-400 transition-colors dark:border-gray-600 dark:bg-gray-600 dark:hover:bg-gray-500"></div>
            </div>
          </div>
        )}

        <div
          ref={outputPanelRef}
          className={cn(
            'flex-1 flex flex-col overflow-y-auto',
            !currentSummary && isMobile && '!hidden'
          )}
        >
          {outputComponentWithConfig}
        </div>
      </div>
    </div>
  );
};

export default TextSummarizerLayout;
